import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
    Bell, 
    Gavel, 
    TrendingUp, 
    MessageCircle, 
    Package, 
    CheckCircle, 
    Star, 
    Clock, 
    Trash2,
    X
} from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import PushNotificationSettings from '@/components/notifications/PushNotificationSettings';

export const NotificationHoverMenu = ({ 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    clearNotifications, 
    removeNotification, 
    isConnected 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [visibleNotifications, setVisibleNotifications] = useState(5);
    const timeoutRef = useRef(null);
    const navigate = useNavigate();

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => setIsOpen(false), 100);
    };

    const handleToggle = () => {
        // On click, always open (mouseLeave will close it)
        setIsOpen(true);
    };

    const handleShowMoreNotifications = () => {
        setVisibleNotifications(prev => prev + 5);
    };
    
    const handleBellClick = () => {
        navigate('/notifications');
        setIsOpen(false);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'bid': return <Gavel className="w-4 h-4 text-primary" />;
            case 'outbid': return <TrendingUp className="w-4 h-4 text-orange-500" />;
            case 'message': return <MessageCircle className="w-4 h-4 text-blue-500" />;
            case 'order': return <Package className="w-4 h-4 text-green-500" />;
            case 'order_update': return <Package className="w-4 h-4 text-purple-500" />;
            case 'order_shipped': return <Package className="w-4 h-4 text-purple-500" />;
            case 'order_delivered': return <Package className="w-4 h-4 text-green-500" />;
            case 'order_completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'payment_confirmed': return <Star className="w-4 h-4 text-green-500" />;
            case 'sale_completed': return <Star className="w-4 h-4 text-green-500" />;
            case 'funds_released': return <Star className="w-4 h-4 text-green-500" />;
            case 'funds_returned': return <Star className="w-4 h-4 text-orange-500" />;
            // case 'escrow_released': return <Star className="w-4 h-4 text-green-500" />; // TEMPORARILY DISABLED
            case 'refund': return <Star className="w-4 h-4 text-orange-500" />;
            case 'auction_ending': return <Clock className="w-4 h-4 text-yellow-500" />;
            case 'auction_won': return <CheckCircle className="w-4 h-4 text-green-500" />;
            default: return <Bell className="w-4 h-4 text-gray-500" />;
        }
    };

    const sortedNotifications = [...notifications].sort((a, b) => 
        new Date(b.timestamp || b.created_at) - new Date(a.timestamp || a.created_at)
    );

    return (
        <div 
            className="relative"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Button 
                variant="ghost" 
                size="icon" 
                className="relative rounded-full"
                onClick={() => navigate('/notifications')}
                onMouseEnter={handleMouseEnter}
                data-testid="notifications-btn"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <Badge 
                        variant="destructive" 
                        className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 text-xs"
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                )}
                {isConnected && (
                    <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border-2 border-white" />
                )}
            </Button>
            
            {isOpen && (
                <div className="fixed sm:absolute top-14 sm:top-full left-2 right-2 sm:left-auto sm:right-[-50px] mt-0 sm:mt-1 sm:w-80 md:w-96 z-[100] rounded-xl border border-gray-200 shadow-lg overflow-hidden bg-white/95 backdrop-blur-sm">
                    {/* Content */}
                    <div>
                    <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b bg-gray-50/80">
                        <h3 className="font-semibold text-sm sm:text-base">Notificări</h3>
                        <div className="flex items-center gap-1 sm:gap-2">
                            {unreadCount > 0 && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-[10px] sm:text-xs h-6 sm:h-7 px-1.5 sm:px-2"
                                    onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                                >
                                    <span className="sm:hidden">✓ Citite</span>
                                    <span className="hidden sm:inline">Marchează citite</span>
                                </Button>
                            )}
                            <PushNotificationSettings compact />
                        </div>
                    </div>
                    
                    <ScrollArea className="h-[280px] sm:h-[300px]">
                        {sortedNotifications.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Nu ai notificări</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {sortedNotifications.slice(0, visibleNotifications).map((notification) => (
                                    <div
                                        key={notification.id || notification.notification_id}
                                        className={`px-2 sm:px-3 py-2 sm:py-3 hover:bg-gray-200 transition-colors relative cursor-pointer mx-1 rounded-lg ${
                                            !notification.read ? 'bg-gray-100' : 'bg-transparent'
                                        }`}
                                    >
                                        <div 
                                            className="flex items-start gap-2 sm:gap-3 pr-6 sm:pr-8"
                                            onClick={() => {
                                                markAsRead(notification.id || notification.notification_id);
                                                if (notification.link) {
                                                    navigate(notification.link);
                                                    setIsOpen(false);
                                                }
                                            }}
                                        >
                                            <div className="mt-0.5 shrink-0">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-xs sm:text-sm line-clamp-1">{notification.title}</p>
                                                <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">
                                                    {formatDateTime(notification.timestamp || notification.created_at)}
                                                </p>
                                            </div>
                                            {!notification.read && (
                                                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 shrink-0" />
                                            )}
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeNotification(notification.id || notification.notification_id); }}
                                            className="absolute top-2 sm:top-3 right-2 sm:right-3 p-1 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                                            title="Închide notificarea"
                                        >
                                            <X className="w-3 h-3 sm:w-4 sm:h-4" />
                                        </button>
                                    </div>
                                ))}
                                {sortedNotifications.length > visibleNotifications && (
                                    <div className="px-2 py-1 text-center">
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="w-full text-xs h-7 text-primary hover:text-blue-700"
                                            onClick={(e) => { e.stopPropagation(); handleShowMoreNotifications(); }}
                                        >
                                            + {sortedNotifications.length - visibleNotifications} mai multe
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </ScrollArea>
                    
                    {sortedNotifications.length > 0 && (
                        <div className="p-2 border-t bg-gray-50/80 flex gap-2">
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="flex-1 text-xs text-primary"
                                onClick={(e) => { e.stopPropagation(); navigate('/notifications'); setIsOpen(false); }}
                            >
                                Vezi toate
                            </Button>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="flex-1 text-xs text-muted-foreground hover:text-destructive"
                                onClick={(e) => { e.stopPropagation(); clearNotifications(); }}
                            >
                                <Trash2 className="w-3 h-3 mr-2" />
                                Șterge toate
                            </Button>
                        </div>
                    )}
                    </div>
                </div>
            )}
        </div>
    );
};
